import React, { useState, useRef, useEffect } from 'react';
import {
  BoltIcon,
  PencilIcon,
  MinusIcon,
  XMarkIcon,
  DocumentTextIcon,
  SwatchIcon,
  Square2StackIcon,
  FaceSmileIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  platform: string;
  maxLength?: number;
  placeholder?: string;
}

interface FormatOption {
  label: string;
  value: string;
  icon?: string;
  supported: string[];
}

const FONT_SIZES: FormatOption[] = [
  { label: 'Small', value: 'small', supported: ['whatsapp', 'telegram'] },
  { label: 'Normal', value: 'normal', supported: ['whatsapp', 'facebook', 'instagram', 'telegram'] },
  { label: 'Large', value: 'large', supported: ['whatsapp', 'telegram'] },
  { label: 'Extra Large', value: 'xlarge', supported: ['telegram'] },
];

const COLORS: FormatOption[] = [
  { label: 'Default', value: 'default', supported: ['whatsapp', 'telegram'] },
  { label: 'Red', value: 'red', supported: ['telegram'] },
  { label: 'Green', value: 'green', supported: ['telegram'] },
  { label: 'Blue', value: 'blue', supported: ['telegram'] },
  { label: 'Yellow', value: 'yellow', supported: ['telegram'] },
  { label: 'Purple', value: 'purple', supported: ['telegram'] },
  { label: 'Orange', value: 'orange', supported: ['telegram'] },
];

const BORDERS: FormatOption[] = [
  { label: 'None', value: 'none', supported: ['whatsapp', 'telegram'] },
  { label: 'Simple', value: 'simple', supported: ['telegram'] },
  { label: 'Dashed', value: 'dashed', supported: ['telegram'] },
  { label: 'Dotted', value: 'dotted', supported: ['telegram'] },
];

const EMOJIS = [
  '🦜', '🐦', '🦅', '🦆', '🦉', '🦇', '🦋', '🦗', '🦎', '🐍',
  '💚', '💙', '💛', '💜', '❤️', '🧡', '🖤', '🤍', '💖', '💝',
  '⭐', '🌟', '✨', '💫', '🔥', '💥', '⚡', '🌈', '☀️', '🌙',
  '📱', '📞', '📧', '💻', '🖥️', '📷', '📹', '🎥', '📺', '📻',
  '🏠', '🏡', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪',
  '🚗', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚',
];

export default function RichTextEditor({ 
  value, 
  onChange, 
  platform, 
  maxLength = 1000,
  placeholder = "Enter your message template..." 
}: RichTextEditorProps) {
  const [showFontSize, setShowFontSize] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [showBorders, setShowBorders] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Get platform-specific formatting options
  const getSupportedFormats = (formats: FormatOption[]) => {
    return formats.filter(format => format.supported.includes(platform));
  };

  const supportedFontSizes = getSupportedFormats(FONT_SIZES);
  const supportedColors = getSupportedFormats(COLORS);
  const supportedBorders = getSupportedFormats(BORDERS);

  // Save current selection
  const saveSelection = () => {
    if (textareaRef.current) {
      setSelection({
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd,
      });
    }
  };

  // Restore selection
  const restoreSelection = () => {
    if (textareaRef.current) {
      textareaRef.current.setSelectionRange(selection.start, selection.end);
      textareaRef.current.focus();
    }
  };

  // Apply formatting to selected text
  const applyFormatting = (format: string, prefix: string = '', suffix: string = '') => {
    if (selection.start === selection.end) return;

    const before = value.substring(0, selection.start);
    const selected = value.substring(selection.start, selection.end);
    const after = value.substring(selection.end);

    const formattedText = `${before}${prefix}${selected}${suffix}${after}`;
    onChange(formattedText);

    // Update selection to include the formatting
    const newStart = selection.start + prefix.length;
    const newEnd = selection.end + prefix.length;
    setSelection({ start: newStart, end: newEnd });

    // Restore focus and selection after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newStart, newEnd);
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Apply platform-specific formatting
  const applyPlatformFormatting = (format: string) => {
    switch (platform) {
      case 'whatsapp':
        switch (format) {
          case 'bold':
            applyFormatting('bold', '*', '*');
            break;
          case 'italic':
            applyFormatting('italic', '_', '_');
            break;
          case 'strikethrough':
            applyFormatting('strikethrough', '~', '~');
            break;
          case 'monospace':
            applyFormatting('monospace', '`', '`');
            break;
        }
        break;
      case 'telegram':
        switch (format) {
          case 'bold':
            applyFormatting('bold', '**', '**');
            break;
          case 'italic':
            applyFormatting('italic', '*', '*');
            break;
          case 'underline':
            applyFormatting('underline', '__', '__');
            break;
          case 'strikethrough':
            applyFormatting('strikethrough', '~~', '~~');
            break;
          case 'monospace':
            applyFormatting('monospace', '`', '`');
            break;
          case 'code':
            applyFormatting('code', '```\n', '\n```');
            break;
        }
        break;
      case 'instagram':
      case 'facebook':
        // Instagram and Facebook don't support text formatting in captions
        // But we can still apply basic formatting for display purposes
        switch (format) {
          case 'bold':
            applyFormatting('bold', '**', '**');
            break;
          case 'italic':
            applyFormatting('italic', '*', '*');
            break;
        }
        break;
    }
  };

  // Apply font size
  const applyFontSize = (size: string) => {
    if (selection.start === selection.end) return;

    const before = value.substring(0, selection.start);
    const selected = value.substring(selection.start, selection.end);
    const after = value.substring(selection.end);

    let formattedText = '';
    switch (platform) {
      case 'telegram':
        switch (size) {
          case 'small':
            formattedText = `${before}<small>${selected}</small>${after}`;
            break;
          case 'large':
            formattedText = `${before}<b>${selected}</b>${after}`;
            break;
          case 'xlarge':
            formattedText = `${before}<b><u>${selected}</u></b>${after}`;
            break;
          default:
            formattedText = `${before}${selected}${after}`;
        }
        break;
      default:
        formattedText = `${before}${selected}${after}`;
    }

    onChange(formattedText);
    setShowFontSize(false);
  };

  // Apply color
  const applyColor = (color: string) => {
    if (selection.start === selection.end) return;

    const before = value.substring(0, selection.start);
    const selected = value.substring(selection.start, selection.end);
    const after = value.substring(selection.end);

    let formattedText = '';
    if (platform === 'telegram') {
      switch (color) {
        case 'red':
          formattedText = `${before}<code>${selected}</code>${after}`;
          break;
        case 'green':
          formattedText = `${before}<pre>${selected}</pre>${after}`;
          break;
        case 'blue':
          formattedText = `${before}<u>${selected}</u>${after}`;
          break;
        default:
          formattedText = `${before}${selected}${after}`;
      }
    } else {
      formattedText = `${before}${selected}${after}`;
    }

    onChange(formattedText);
    setShowColors(false);
  };

  // Apply border
  const applyBorder = (border: string) => {
    if (selection.start === selection.end) return;

    const before = value.substring(0, selection.start);
    const selected = value.substring(selection.start, selection.end);
    const after = value.substring(selection.end);

    let formattedText = '';
    if (platform === 'telegram') {
      switch (border) {
        case 'simple':
          formattedText = `${before}┌─${selected}─┐${after}`;
          break;
        case 'dashed':
          formattedText = `${before}┌┄┄${selected}┄┄┐${after}`;
          break;
        case 'dotted':
          formattedText = `${before}┌┈┈${selected}┈┈┐${after}`;
          break;
        default:
          formattedText = `${before}${selected}${after}`;
      }
    } else {
      formattedText = `${before}${selected}${after}`;
    }

    onChange(formattedText);
    setShowBorders(false);
  };

  // Insert emoji
  const insertEmoji = (emoji: string) => {
    const before = value.substring(0, selection.start);
    const after = value.substring(selection.end);

    const newText = `${before}${emoji}${after}`;
    onChange(newText);

    // Update selection to after the emoji
    const newPosition = selection.start + emoji.length;
    setSelection({ start: newPosition, end: newPosition });

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newPosition, newPosition);
        textareaRef.current.focus();
      }
    }, 0);

    setShowEmojis(false);
    setEmojiSearch(''); // Clear search when emoji is inserted
  };

  // Get character count with formatting
  const getCharacterCount = () => {
    // Remove formatting characters for accurate count
    let cleanText = value;
    if (platform === 'whatsapp') {
      cleanText = value.replace(/[*_~`]/g, '');
    } else if (platform === 'telegram') {
      cleanText = value.replace(/[*_~`<>]/g, '');
    }
    return cleanText.length;
  };

  // Filter emojis based on search
  const filteredEmojis = EMOJIS.filter(emoji => 
    emojiSearch === '' || emoji.includes(emojiSearch)
  );

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50/80 border border-gray-200 rounded-lg">
        {/* Text Formatting */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => applyPlatformFormatting('bold')}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded font-bold"
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => applyPlatformFormatting('italic')}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded italic"
            title="Italic"
          >
            I
          </button>
          {platform === 'telegram' && (
            <button
              type="button"
              onClick={() => applyPlatformFormatting('underline')}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded underline"
              title="Underline"
            >
              U
            </button>
          )}
          <button
            type="button"
            onClick={() => applyPlatformFormatting('strikethrough')}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded line-through"
            title="Strikethrough"
          >
            S
          </button>
        </div>

        <div className="w-px h-6 bg-gray-300"></div>

        {/* Font Size */}
        {supportedFontSizes.length > 1 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowFontSize(!showFontSize)}
              className="flex items-center gap-1 p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded"
              title="Font Size"
            >
              <DocumentTextIcon className="h-4 w-4" />
              <ChevronDownIcon className="h-3 w-3" />
            </button>
            {showFontSize && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                {supportedFontSizes.map((size) => (
                  <button
                    key={size.value}
                    type="button"
                    onClick={() => applyFontSize(size.value)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Colors */}
        {supportedColors.length > 1 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowColors(!showColors)}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded"
              title="Text Color"
            >
              <SwatchIcon className="h-4 w-4" />
            </button>
            {showColors && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                {supportedColors.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => applyColor(color.value)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  >
                    {color.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Borders */}
        {supportedBorders.length > 1 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowBorders(!showBorders)}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded"
              title="Border Frame"
            >
              <Square2StackIcon className="h-4 w-4" />
            </button>
            {showBorders && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                {supportedBorders.map((border) => (
                  <button
                    key={border.value}
                    type="button"
                    onClick={() => applyBorder(border.value)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                  >
                    {border.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="w-px h-6 bg-gray-300"></div>

        {/* Emojis */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowEmojis(!showEmojis)}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-gray-200 rounded"
            title="Insert Emoji"
          >
            <FaceSmileIcon className="h-4 w-4" />
          </button>
          {showEmojis && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-w-[400px] max-h-[350px] overflow-hidden">
              <div className="p-3 border-b border-gray-200 bg-slate-50/80">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Select Emoji</h4>
                <input
                  type="text"
                  placeholder="Search emojis..."
                  value={emojiSearch}
                  onChange={(e) => setEmojiSearch(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="p-4 max-h-[280px] overflow-y-auto">
                {filteredEmojis.length > 0 ? (
                  <div className="grid grid-cols-8 gap-2">
                    {filteredEmojis.map((emoji, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => insertEmoji(emoji)}
                        className="p-2 text-xl hover:bg-cyan-50 hover:border-blue-200 border border-transparent rounded transition-all duration-150 flex items-center justify-center min-w-[40px] min-h-[40px]"
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <p className="text-sm">No emojis found for "{emojiSearch}"</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Textarea */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={saveSelection}
          onBlur={saveSelection}
          rows={8}
          maxLength={maxLength}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
        />
        
        {/* Character count */}
        <div className="mt-2 flex justify-between text-sm text-slate-500">
          <span>
            Character count: {getCharacterCount()}/{maxLength}
          </span>
          <span className="text-xs">
            Platform: {platform.charAt(0).toUpperCase() + platform.slice(1)}
          </span>
        </div>

        {/* Platform formatting info */}
        <div className="mt-2 p-2 bg-cyan-50 border border-blue-200 rounded text-xs text-cyan-700">
          <strong>Formatting Support:</strong>
          {platform === 'whatsapp' && ' Bold (*text*), Italic (_text_), Strikethrough (~text~), Monospace (`text`)'}
          {platform === 'telegram' && ' Bold (**text**), Italic (*text*), Underline (__text__), Strikethrough (~~text~~), Monospace (`text`), Code blocks (```code```)'}
          {platform === 'instagram' && ' Limited formatting support in captions'}
          {platform === 'facebook' && ' Limited formatting support in posts'}
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showFontSize || showColors || showBorders || showEmojis) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowFontSize(false);
            setShowColors(false);
            setShowBorders(false);
            setShowEmojis(false);
            setEmojiSearch(''); // Clear search when picker is closed
          }}
        />
      )}
    </div>
  );
}
